import { useEffect, useState, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { collection, query, orderBy, onSnapshot } from "firebase/firestore";
import { callFunction } from "../api/callFunction";
import { toast } from "react-toastify";
import { db } from "../firebase";
import { GlobalContext } from "../context/GlobalContext";
import {
  toMillis,
  formatRelativeTime,
  formatTimestamp,
} from "../components/MessageThread";

// eventFeedback: member event-EXPERIENCE feedback captured through "Ask Gist AI" — how
// an event they attended felt, plus repeat-attendees' OVERALL take on Gist. Distinct
// from Issues (bug/platform reports) and Arbitration (safety incidents). Mirrors the
// Issues view: live list + detail pane, search/filter, callable mutation.
//
// "How was it?" (the day-after rating) mirrors each answered review here too, as
// review_{eventId}_{uid} with source "how_was_it", so it gets the same triage.

const STATUS_META = {
  new: { label: "New", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" },
  reviewed: { label: "Reviewed", cls: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" },
  actioned: { label: "Actioned", cls: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" },
};

const SENTIMENT_META = {
  positive: { label: "Positive", cls: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" },
  mixed: { label: "Mixed", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" },
  negative: { label: "Negative", cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" },
};

const SCOPE_META = {
  event: { label: "Event", cls: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300" },
  overall: { label: "Overall", cls: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300" },
};

// Where a row came from, when it is not Gist AI (which has no badge, as before).
export const SOURCE_META = {
  how_was_it: { label: "How was it?", cls: "bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300" },
};

function Badge({ cls, children }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${cls}`}>
      {children}
    </span>
  );
}

// A 1-5 rating as filled/empty stars; renders nothing if no rating was captured.
function Stars({ value }) {
  if (typeof value !== "number" || value < 1) return null;
  const n = Math.max(1, Math.min(5, Math.round(value)));
  return (
    <span className="text-amber-500 text-sm" title={`${n} / 5`}>
      {"★".repeat(n)}
      <span className="text-gray-300 dark:text-gray-600">{"★".repeat(5 - n)}</span>
    </span>
  );
}

// A readable title for a feedback row: the event's name for event-scoped feedback,
// otherwise an "overall experience" label. Keeps the list scannable (eventFeedback
// docs carry no title field of their own).
function feedbackTitle(f) {
  if (f.scope === "overall") return "Overall Gist experience";
  return f.eventName || "Event feedback";
}

// A member's words as one string. Gist AI writes `comments` as a string; a
// "How was it?" mirror carries every comment the member sent, which may arrive
// as a list, so each entry gets its own paragraph.
export function commentsText(f) {
  const raw = f?.comments;
  if (typeof raw === "string") return raw;
  if (!Array.isArray(raw)) return "";
  return raw
    .map((entry) => (typeof entry === "string" ? entry : entry?.text))
    .filter((text) => typeof text === "string" && text.trim())
    .join("\n\n");
}

export default function Feedback() {
  const { globalState } = useContext(GlobalContext);
  const isSuperAdmin = globalState.isSuperAdmin;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [newOnly, setNewOnly] = useState(false);

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");

  // Live list of all feedback (superAdmin-only read, gated by rules).
  useEffect(() => {
    if (!isSuperAdmin) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const q = query(collection(db, "eventFeedback"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => {
          const data = d.data();
          return { id: d.id, ...data, comments: commentsText(data), _ms: toMillis(data.createdAt) };
        });
        setItems(rows);
        setLoading(false);
      },
      (err) => {
        console.error("Error loading event feedback:", err);
        setLoadError(err?.message || "Failed to load feedback.");
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [isSuperAdmin]);

  const selected = useMemo(
    () => items.find((f) => f.id === selectedId) || null,
    [items, selectedId]
  );

  const newCount = useMemo(
    () => items.filter((f) => (f.status || "new") === "new").length,
    [items]
  );

  const filtered = useMemo(() => {
    let list = items;
    if (newOnly) list = list.filter((f) => (f.status || "new") === "new");
    const ql = searchQuery.trim().toLowerCase();
    if (!ql) return list;
    return list.filter(
      (f) =>
        (f.comments && f.comments.toLowerCase().includes(ql)) ||
        (f.highlights && f.highlights.toLowerCase().includes(ql)) ||
        (f.improvements && f.improvements.toLowerCase().includes(ql)) ||
        (f.eventName && f.eventName.toLowerCase().includes(ql)) ||
        (f.reporterName && f.reporterName.toLowerCase().includes(ql))
    );
  }, [items, searchQuery, newOnly]);

  const callUpdate = async (patch) => {
    if (!selectedId) return;
    setSaving(true);
    try {
      await callFunction("adminUpdateEventFeedback", { feedbackId: selectedId, ...patch }, { timeout: 60000 });
    } catch (err) {
      console.error("adminUpdateEventFeedback failed:", err);
      toast.error(err?.message || "Update failed.");
    } finally {
      setSaving(false);
    }
  };

  const submitNote = () => {
    const text = noteDraft.trim();
    if (!text) return;
    callUpdate({ addNote: text });
    setNoteDraft("");
  };

  const callDelete = async () => {
    if (!selectedId) return;
    if (
      !window.confirm(
        `Delete the feedback "${feedbackTitle(selected)}"? This permanently removes it and cannot be undone.`
      )
    )
      return;
    setDeleting(true);
    try {
      await callFunction("adminDeleteEventFeedback", { feedbackId: selectedId }, { timeout: 60000 });
      toast.success("Feedback deleted.");
      setSelectedId(null);
    } catch (err) {
      console.error("adminDeleteEventFeedback failed:", err);
      toast.error(err?.message || "Delete failed.");
    } finally {
      setDeleting(false);
    }
  };

  const openUser = (userID) => {
    if (!userID) return;
    navigate(`/users?q=${encodeURIComponent(userID)}`);
  };

  if (!isSuperAdmin) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 mt-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Feedback</h1>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          This view is only available to super admins.
        </p>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="mt-4 sm:mt-0">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Feedback</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          How members felt about events they attended, from Ask Gist AI and the "How was it?" text the day after.
          {items.length > 0 && <span> {items.length} total.</span>}
        </p>
      </div>

      <div className="mt-4 flex flex-col lg:flex-row gap-4 h-[calc(100vh-180px)]">
        {/* Feedback list */}
        <div className="lg:w-80 flex-shrink-0 flex flex-col border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 overflow-hidden">
          <div className="p-3 border-b border-gray-200 dark:border-gray-700 space-y-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search comments, event, member…"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
            />
            <button
              onClick={() => setNewOnly((v) => !v)}
              className={`w-full px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                newOnly
                  ? "bg-amber-500 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              }`}
            >
              {newOnly ? "Showing new" : "New only"}
              {newCount > 0 && ` (${newCount})`}
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
              </div>
            ) : loadError ? (
              <div className="p-4 text-sm text-red-600 dark:text-red-400">{loadError}</div>
            ) : filtered.length === 0 ? (
              <div className="p-4 text-sm text-gray-400 dark:text-gray-500 text-center">No feedback found.</div>
            ) : (
              filtered.map((f) => {
                const isSelected = f.id === selectedId;
                const sm = STATUS_META[f.status] || STATUS_META.new;
                const sent = f.sentiment ? SENTIMENT_META[f.sentiment] : null;
                const scope = SCOPE_META[f.scope] || SCOPE_META.event;
                const source = SOURCE_META[f.source];
                return (
                  <button
                    key={f.id}
                    onClick={() => setSelectedId(f.id)}
                    className={`w-full text-left px-3 py-3 border-b border-gray-100 dark:border-gray-800 transition-colors ${
                      isSelected ? "bg-primary/10" : "hover:bg-gray-50 dark:hover:bg-gray-700"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                        {feedbackTitle(f)}
                      </span>
                      <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                        {formatRelativeTime(f._ms)}
                      </span>
                    </div>
                    {f.comments && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">{f.comments}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-1.5 mt-1">
                      <Badge cls={sm.cls}>{sm.label}</Badge>
                      <Badge cls={scope.cls}>{scope.label}</Badge>
                      {source && <Badge cls={source.cls}>{source.label}</Badge>}
                      {sent && <Badge cls={sent.cls}>{sent.label}</Badge>}
                      <Stars value={f.rating} />
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Detail pane */}
        <div className="flex-1 flex flex-col border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 overflow-hidden min-h-0">
          {!selected ? (
            <div className="flex-1 flex items-center justify-center text-gray-400 dark:text-gray-500 text-sm">
              Select feedback to review it.
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto p-4 space-y-5">
              {/* Header */}
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900 dark:text-gray-100 text-lg">
                    {feedbackTitle(selected)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    shared {formatRelativeTime(selected._ms)}
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0 flex-wrap justify-end">
                  <Badge cls={(STATUS_META[selected.status] || STATUS_META.new).cls}>
                    {(STATUS_META[selected.status] || STATUS_META.new).label}
                  </Badge>
                  <Badge cls={(SCOPE_META[selected.scope] || SCOPE_META.event).cls}>
                    {(SCOPE_META[selected.scope] || SCOPE_META.event).label}
                  </Badge>
                  {SOURCE_META[selected.source] && (
                    <Badge cls={SOURCE_META[selected.source].cls}>{SOURCE_META[selected.source].label}</Badge>
                  )}
                  {selected.sentiment && SENTIMENT_META[selected.sentiment] && (
                    <Badge cls={SENTIMENT_META[selected.sentiment].cls}>
                      {SENTIMENT_META[selected.sentiment].label}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Feedback details */}
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">From</p>
                    <p className="text-sm text-gray-900 dark:text-gray-100">
                      {selected.reporterName || "Unknown member"}
                      {selected.uid && (
                        <button
                          className="ml-2 text-primary hover:underline text-xs font-mono"
                          onClick={() => openUser(selected.uid)}
                          title="Open in Users"
                        >
                          {selected.uid}
                        </button>
                      )}
                    </p>
                  </div>
                  {typeof selected.rating === "number" && (
                    <div className="text-right">
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">Rating</p>
                      <Stars value={selected.rating} />
                    </div>
                  )}
                </div>
                {selected.scope === "event" && (selected.eventName || selected.eventId) && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">Event</p>
                    <p className="text-sm text-gray-900 dark:text-gray-100">
                      {selected.eventName || "(unnamed)"}
                      {selected.eventId && (
                        <span className="ml-2 text-xs font-mono text-gray-400 dark:text-gray-500">
                          {selected.eventId}
                        </span>
                      )}
                    </p>
                  </div>
                )}
                {selected.highlights && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">What stood out</p>
                    <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                      {selected.highlights}
                    </p>
                  </div>
                )}
                {selected.improvements && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">What could be better</p>
                    <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                      {selected.improvements}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">In their words</p>
                  <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                    {selected.comments || "(none)"}
                  </p>
                </div>
              </div>

              {/* Triage controls */}
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-3">
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">Triage</p>
                <label className="block text-sm">
                  <span className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Status</span>
                  <select
                    value={selected.status || "new"}
                    disabled={saving}
                    onChange={(e) => callUpdate({ status: e.target.value })}
                    className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  >
                    <option value="new">New</option>
                    <option value="reviewed">Reviewed</option>
                    <option value="actioned">Actioned</option>
                  </select>
                </label>

                {/* Admin notes log */}
                {Array.isArray(selected.adminNotes) && selected.adminNotes.length > 0 && (
                  <div className="space-y-1.5">
                    <span className="block text-xs text-gray-500 dark:text-gray-400">Notes</span>
                    {[...selected.adminNotes]
                      .sort((a, b) => (a.at || 0) - (b.at || 0))
                      .map((n, i) => (
                        <div
                          key={i}
                          className="text-sm text-gray-700 dark:text-gray-200 bg-gray-50 dark:bg-gray-900 rounded px-2 py-1"
                        >
                          <span className="text-[10px] text-gray-400 dark:text-gray-500">
                            {formatTimestamp(n.at)}
                          </span>
                          <p className="whitespace-pre-wrap">{n.text}</p>
                        </div>
                      ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={noteDraft}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    placeholder="Add an internal note…"
                    className="flex-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") submitNote();
                    }}
                  />
                  <button
                    onClick={submitNote}
                    disabled={saving || !noteDraft.trim()}
                    className="px-3 py-1.5 rounded-md text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 disabled:opacity-40"
                  >
                    Add note
                  </button>
                </div>
              </div>

              {/* Danger zone */}
              <div className="rounded-lg border border-red-200 dark:border-red-900/50 p-3">
                <p className="text-sm font-semibold text-red-700 dark:text-red-400">Danger zone</p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Permanently delete this feedback. This cannot be undone.
                </p>
                <button
                  onClick={callDelete}
                  disabled={deleting}
                  className="mt-2 px-3 py-1.5 rounded-md text-sm font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-40"
                >
                  {deleting ? "Deleting…" : "Delete feedback"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
